import {
  AGE_CATEGORIES,
  SESSIONS,
  SPECIALTIES,
  SUPERVISION_LEVELS,
} from "./catalogues";

export function normalizedKey(value: unknown): string {
  return String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("en-GB");
}

const specialtyAliases: Readonly<Record<string, string>> = {
  gynae: "Gynaecology",
  gynaecology: "Gynaecology",
  gynecology: "Gynaecology",
  paeds: "Paediatrics",
  paediatric: "Paediatrics",
  paediatrics: "Paediatrics",
  pediatric: "Paediatrics",
  pediatrics: "Paediatrics",
  ortho: "Orthopaedics",
  orthopaedic: "Orthopaedics",
  orthopaedics: "Orthopaedics",
  orthopedic: "Orthopaedics",
  orthopedics: "Orthopaedics",
  uro: "Urology",
  urology: "Urology",
  obstetric: "Obstetrics",
  obstetrics: "Obstetrics",
  obs: "Obstetrics",
  neonatal: "Neonates",
  neonate: "Neonates",
  neonates: "Neonates",
  ent: "ENT",
  cardiac: "Cardiac",
  dental: "Dental",
  general: "General",
  ophth: "Ophthalmics",
  ophthalmic: "Ophthalmics",
  ophthalmics: "Ophthalmics",
  ophthalmology: "Ophthalmics",
  neuro: "Neurosurgery",
  neurosurgery: "Neurosurgery",
  hpb: "Hepatobiliary",
  hepatobiliary: "Hepatobiliary",
  bariatric: "Bariatric",
  breast: "Breast",
  vascular: "Vascular",
  thoracic: "Thoracic",
  trauma: "Trauma",
  maxfac: "Maxillo-facial",
  maxfax: "Maxillo-facial",
  maxillofacial: "Maxillo-facial",
  plastics: "Plastics/burns",
  burns: "Plastics/burns",
  "plastics burns": "Plastics/burns",
  pain: "Chronic Pain",
  "chronic pain": "Chronic Pain",
  radiology: "Radiology",
  resuscitation: "Resuscitation",
  emergency: "Resuscitation",
  transfer: "Transfer",
  colorectal: "General",
  "upper gi": "General",
  "upper gastrointestinal": "General",
  transplant: "General",
  "perioperative medicine": "Other",
  icu: "Other",
  "intensive care": "Other",
  misc: "Other",
  miscellaneous: "Other",
  other: "Other",
};

export function normalizeSpecialty(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const key = normalizedKey(raw);
  const alias = specialtyAliases[key];
  if (alias) return alias;
  return SPECIALTIES.find((candidate) => normalizedKey(candidate) === key) ?? raw;
}

export function normalizeSession(value: unknown, fallback = ""): string {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  const key = normalizedKey(raw);
  if (["am", "morning", "0800 1300", "8 13"].includes(key)) return "Morning";
  if (["pm", "afternoon", "1300 1800", "13 18"].includes(key)) return "Afternoon";
  if (["eve", "evening", "1800 2200", "18 22"].includes(key)) return "Evening";
  if (["night", "overnight", "2200 0800", "22 8"].includes(key)) return "Night";
  const labels: Array<[RegExp, string]> = [
    [/\bmorning\b/, "Morning"],
    [/\bafternoon\b/, "Afternoon"],
    [/\b(?:evening|eve)\b/, "Evening"],
    [/\bnight\b/, "Night"],
  ];
  const firstLabel = labels
    .map(([pattern, label]) => ({ index: key.search(pattern), label }))
    .filter(({ index }) => index >= 0)
    .sort((left, right) => left.index - right.index)[0];
  if (firstLabel) return firstLabel.label;

  const compacted = raw.toLocaleLowerCase("en-GB").replace(/[:\s]/g, "");
  if (compacted.includes("0800-1300") || compacted.includes("800-1300")) return "Morning";
  if (compacted.includes("1300-1800")) return "Afternoon";
  if (compacted.includes("1800-2200")) return "Evening";
  if (compacted.includes("2200-0800") || compacted.includes("2200-800")) return "Night";
  if (key.includes("pm")) return "Afternoon";
  return SESSIONS.find((candidate) => normalizedKey(candidate) === key) ?? fallback;
}

export function normalizeSupervision(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const compact = normalizedKey(raw).replace(/\s+/g, "");
  const direct = SUPERVISION_LEVELS.find((level) => compact === level);
  if (direct) return direct;
  if (/^(immediate|direct)/.test(compact)) return "1";
  if (/^(local)/.test(compact)) return "2a";
  if (/^(distant|remote)/.test(compact)) return "3";
  if (/^(level)?1|direct/.test(compact)) return "1";
  if (/2a|theatre(suite)?/.test(compact)) return "2a";
  if (/2b|withinhospital|in-hospital/.test(compact)) return "2b";
  if (/^(level)?3|oncall|offsite|home/.test(compact)) return "3";
  if (/^(level)?4|independent|solo/.test(compact)) return "4";
  return raw;
}

export function normalizeAsa(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const withoutPrefix = raw.toUpperCase().replace(/^ASA[\s_-]*/, "").trim();
  if (/^[1-5]$/.test(withoutPrefix)) return withoutPrefix;
  if (["DONOR", "D"].includes(withoutPrefix)) return "Donor";
  return raw;
}

export function normalizeExplicitDayCase(value: unknown): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  const raw = String(value ?? "").trim();
  const key = normalizedKey(raw);
  if (["yes", "y", "true", "1", "day case", "daycase"].includes(key)) return "Yes";
  if (["no", "n", "false", "0", "not day case"].includes(key)) return "No";
  return "";
}

const legacyAgeAliases: Readonly<Record<string, string>> = {
  neonate: "<28 days neonate",
  neonatal: "<28 days neonate",
  "neonate (<28d)": "<28 days neonate",
  "<28d": "<28 days neonate",
  "<28 days": "<28 days neonate",
  infant: "<1 yr",
  "infant (1 12m)": "<1 yr",
  child: "1-4 yrs",
  paediatric: "1-4 yrs",
  pediatric: "1-4 yrs",
  "1 5 yrs": "1-4 yrs",
  "1 4": "1-4 yrs",
  "5 9": "5-9 yrs",
  "6 15 yrs": "10-17 yrs",
  "6 12 yrs": "10-17 yrs",
  "10 17": "10-17 yrs",
  "16 59 yrs": "18-59 yrs",
  "18 59": "18-59 yrs",
  "40 59 yrs": "18-59 yrs",
  adult: "18-59 yrs",
  "60 69 yrs": "60-79 yrs",
  "70 79 yrs": "60-79 yrs",
  elderly: "60-79 yrs",
  "80+": "≥80 yrs",
  "80+ yrs": "≥80 yrs",
  ">=80": "≥80 yrs",
};

export function normalizeAgeCategory(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const key = normalizedKey(raw);
  const alias = legacyAgeAliases[key];
  if (alias) return alias;
  if (key.includes("neonate")) return "<28 days neonate";
  if (key.includes("infant")) return "<1 yr";
  return AGE_CATEGORIES.find((candidate) => normalizedKey(candidate) === key) ?? raw;
}

type ExactAgeUnit = "days" | "weeks" | "months" | "years";

function parseDetailedAge(value: unknown): { amount: number; unit: ExactAgeUnit; hasUnit: boolean } | null {
  const raw = String(value ?? "").trim();
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*(d|day|days|w|wk|wks|week|weeks|mo|mos|mth|mths|month|months|y|yr|yrs|year|years)?$/i);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount < 0) return null;
  const token = (match[2] ?? "").toLocaleLowerCase("en-GB");
  const unit: ExactAgeUnit = ["d", "day", "days"].includes(token)
    ? "days"
    : ["w", "wk", "wks", "week", "weeks"].includes(token)
      ? "weeks"
      : ["mo", "mos", "mth", "mths", "month", "months"].includes(token)
        ? "months"
        : "years";
  return { amount, unit, hasUnit: Boolean(match[2]) };
}

function approximateAgeDays(amount: number, unit: ExactAgeUnit): number {
  if (unit === "days") return amount;
  if (unit === "weeks") return amount * 7;
  if (unit === "months") return amount * 30.4375;
  return amount * 365.25;
}

function reportBandForDays(days: number): string {
  if (days < 28) return "<28 days neonate";
  if (days < 365.25) return "<1 yr";
  const years = days / 365.25;
  if (years < 5) return "1-4 yrs";
  if (years < 10) return "5-9 yrs";
  if (years < 18) return "10-17 yrs";
  if (years < 60) return "18-59 yrs";
  if (years < 80) return "60-79 yrs";
  return "≥80 yrs";
}

export function reportAgeBand(age: unknown): string {
  const raw = String(age ?? "").trim();
  const normalized = normalizeAgeCategory(raw);
  if (AGE_CATEGORIES.includes(normalized as (typeof AGE_CATEGORIES)[number])) return normalized;

  const exact = parseDetailedAge(normalized);
  return exact ? reportBandForDays(approximateAgeDays(exact.amount, exact.unit)) : normalized || "Not recorded";
}

export function normalizeImportedAge(age: unknown): string {
  const raw = String(age ?? "").trim();
  if (!raw) return "";
  const normalized = normalizeAgeCategory(raw);
  if (AGE_CATEGORIES.includes(normalized as (typeof AGE_CATEGORIES)[number])) return normalized;

  const exact = parseDetailedAge(raw);
  if (!exact) return normalized;
  if (Number.isInteger(exact.amount)) return `${exact.amount} ${exact.unit}`;

  const convertedAmount = exact.unit === "years"
    ? exact.amount * 12
    : exact.unit === "weeks"
      ? exact.amount * 7
      : Number.NaN;
  const convertedUnit: ExactAgeUnit = exact.unit === "years" ? "months" : "days";
  if (Number.isInteger(convertedAmount)) return `${convertedAmount} ${convertedUnit}`;

  return exact.hasUnit ? raw : `${raw} years`;
}

export function formatStorageDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function defaultClinicalDate(now = new Date(), resetHour = 7): string {
  const clinical = new Date(now);
  if (clinical.getHours() < resetHour) clinical.setDate(clinical.getDate() - 1);
  return formatStorageDate(clinical);
}

export function defaultSession(now = new Date()): string {
  const hour = now.getHours();
  if (hour >= 7 && hour < 12) return "Morning";
  if (hour >= 12 && hour < 17) return "Afternoon";
  if (hour >= 17 && hour < 22) return "Evening";
  return "Night";
}
